import { QueryObserverOptions } from "@tanstack/react-query"

import { MAX_LIMIT } from "@shared/enums/pages"
import {
  CreateCareGroupRequest,
  UpdateCareGroupCommunicationRequest,
  UpdateCareGroupExternalDomainsRequest,
  UpdateCareGroupRequest,
  UpdateCareGroupThemeRequest,
} from "@shared/zod/schemas/requests/careGroup"
import { PatchCareGroupAuthorizationRequest } from "@shared/zod/schemas/requests/careGroupAuthorizationRequests"
import { FileRequest } from "@shared/zod/schemas/requests/file"
import { PageRequest } from "@shared/zod/schemas/requests/page"
import { PostalCodeAreaPc4Request, PostalCodeAreaRequest } from "@shared/zod/schemas/requests/postalCodeArea"
import { CareGroupAuthorizationPageResponse } from "@shared/zod/schemas/responses/careGroup"
import {
  CareGroupAuthorizationResponse,
  CareGroupDetailedResponse,
  CareGroupPageResponse,
  CareGroupPortalDetailedResponse,
  CareGroupResponse,
} from "@shared/zod/schemas/responses/careGroup"

import { axiosAuth, axiosPublic } from "./generic/AxiosInterceptor/AxiosInterceptor"

// Sets defaults used in `buildApiFromServices`
export const queryOptions: Partial<QueryObserverOptions> = {
  staleTime: 5 * 60 * 1000, // 5 minutes
}

/**
 * ===========================================================
 * Public end-points
 * ===========================================================
 */

/** Fetch care group by id */
export const publicGetById = async (id: string): Promise<CareGroupDetailedResponse> => {
  const resp = await axiosPublic({
    url: `/public/care-groups/${id}`,
    method: "get",
  })
  return resp.data
}

/** Fetch care group by `slug` */
export const publicGetBySlug = async (slug: string): Promise<CareGroupDetailedResponse> => {
  const resp = await axiosPublic({
    url: `/public/care-groups/slugs/${slug}`,
    method: "get",
  })
  return resp.data
}

/** Fetch the care group registered for the given external `host` */
export const publicGetByDomain = async (): Promise<CareGroupDetailedResponse> => {
  // The backend reads the caller's hostname from the (browser-set, unforgeable)
  // `Origin` header rather than a query parameter — see `getByDomain` on
  // `CareGroupsPublicController`.
  const resp = await axiosPublic({
    url: "/public/care-groups/by-domain",
    method: "get",
  })
  return resp.data
}

/**
 * ===========================================================
 * Authenticated end-points
 * ===========================================================
 */

/** Fetches all care groups */
export const getAll = async (): Promise<CareGroupResponse[]> => {
  const resp = await axiosAuth({
    url: `/care-groups`,
    method: "get",
    params: {
      limit: MAX_LIMIT,
    },
  })

  const pageResponse: CareGroupPageResponse = resp.data
  return pageResponse.data ?? []
}

/** Fetches (paginated/filtered) overview of care groups */
export const findAll = async (query?: PageRequest): Promise<CareGroupPageResponse> => {
  const resp = await axiosAuth({
    url: "/care-groups",
    method: "get",
    params: query,
  })
  return resp.data
}

export const getById = async (id: string): Promise<CareGroupPortalDetailedResponse> => {
  const resp = await axiosAuth({
    url: `/care-groups/${id}`,
    method: "get",
  })
  return resp.data
}

export const patch = async (id: string, data: UpdateCareGroupRequest): Promise<CareGroupResponse> => {
  const resp = await axiosAuth({
    url: `/care-groups/${id}`,
    method: "patch",
    data,
  })
  return resp.data
}

/** Adds new logo to a care group */
export const addLogo = async (id: string, data: FileRequest): Promise<void> => {
  return await axiosAuth({
    url: `/care-groups/${id}/logo`,
    method: "post",
    data,
  })
}

/** Removes the logo of a care group */
export const deleteLogo = async (id: string): Promise<void> => {
  return await axiosAuth({
    url: `/care-groups/${id}/logo`,
    method: "delete",
  })
}

/** Replaces the care group's public-page theme; an all-empty request clears it */
export const patchTheme = async (id: string, data: UpdateCareGroupThemeRequest): Promise<void> => {
  await axiosAuth({
    url: `/care-groups/${id}/theme`,
    method: "patch",
    data,
  })
}

/** Adds a new public-page illustration to a care group */
export const addIllustration = async (id: string, data: FileRequest): Promise<void> => {
  return await axiosAuth({
    url: `/care-groups/${id}/illustration`,
    method: "post",
    data,
  })
}

/** Removes the public-page illustration of a care group */
export const deleteIllustration = async (id: string): Promise<void> => {
  return await axiosAuth({
    url: `/care-groups/${id}/illustration`,
    method: "delete",
  })
}

/** Fetches the dashboard database for a care group */
export const getDashboardDatabase = async (id: string): Promise<Blob> => {
  const resp = await axiosAuth({
    url: `/care-groups/${id}/dashboard-database`,
    method: "get",
    responseType: "blob",
    timeout: 0, // Disable timeout for potentially long-running request
  })
  return resp.data
}

/** Patches (or upserts) the care group communication template by key (for both languages) */
export const patchCommunicationTemplate = async (
  id: string,
  data: UpdateCareGroupCommunicationRequest
): Promise<CareGroupResponse> => {
  const resp = await axiosAuth({
    url: `/care-groups/${id}/communication`,
    method: "patch",
    data,
  })
  return resp.data
}

/**
 * Updates the care group's service area *filter* (Layer 2): the narrowing area
 * editable by any care-group user with configuration access. Narrows, never widens,
 * the admin-set service area (Layer 1).
 */
export const patchServiceAreaFilter = async (id: string, data: PostalCodeAreaPc4Request): Promise<void> => {
  await axiosAuth({
    url: `/care-groups/${id}/service-area-filter`,
    method: "patch",
    data,
  })
}

/**
 * ===========================================================
 * Care group "user authorization" end-points
 * ===========================================================
 */

export const getAllCareGroupAuthorizations = async (id: string): Promise<CareGroupAuthorizationResponse[]> => {
  const resp = await axiosAuth<CareGroupAuthorizationPageResponse | undefined>({
    url: `/care-groups/${id}/authorizations`,
    method: "get",
  })

  const { data } = resp

  return data?.data ?? []
}

export const patchCareGroupAuthorization = async (
  id: string,
  authorizationId: string,
  data: PatchCareGroupAuthorizationRequest
): Promise<void> => {
  await axiosAuth({
    url: `/care-groups/${id}/authorizations/${authorizationId}`,
    method: "patch",
    data,
  })
  return undefined
}

export const removeCareGroupAuthorization = async (id: string, authorizationId: string): Promise<void> => {
  await axiosAuth({
    url: `/care-groups/${id}/authorizations/${authorizationId}`,
    method: "delete",
  })
  return undefined
}

/**
 * ===========================================================
 * Admin only end-points
 * ===========================================================
 */

/** Creates new care group */
export const create = async (data: CreateCareGroupRequest): Promise<CareGroupResponse> => {
  const resp = await axiosAuth({
    url: "/admin/care-groups",
    method: "post",
    data,
  })
  return resp.data
}

/**
 * Rebuilds the care group's dashboard database snapshot right away, instead of
 * waiting for the next scheduled run (admin only). The backend builds inline,
 * so — like the download above — the request needs an unbounded timeout.
 */
export const rebuildDashboardDatabase = async (id: string): Promise<void> => {
  await axiosAuth({
    url: `/admin/care-groups/${id}/dashboard-database`,
    method: "post",
    timeout: 0,
  })
}

/** Updates the service area of a care group (admin only) */
export const patchServiceArea = async (id: string, data: PostalCodeAreaRequest): Promise<void> => {
  await axiosAuth({
    url: `/admin/care-groups/${id}/service-area`,
    method: "patch",
    data,
  })
}

/** Updates the external domains that serve a care group's public landing page directly (admin only) */
export const patchExternalDomains = async (id: string, data: UpdateCareGroupExternalDomainsRequest): Promise<void> => {
  await axiosAuth({
    url: `/admin/care-groups/${id}/external-domains`,
    method: "patch",
    data,
  })
}
